import LoadingBox from '@/components/Loading/LoadingBox'
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getService,
    getServiceNotificationMailers,
    getServiceNotifications,
} from '@/modules/Service/services'
import {
    DNSManagementType,
    MetadataKeyType,
    ServiceNotification,
    ServiceNotificationProps,
} from '@/modules/Service/types'
import { useQueries } from '@tanstack/react-query'
import { Server } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { AlertError } from 'vnetwork-ui'
import Create from './Create'
import List from './List'
import ServiceNotificationMailer from './MailerNotification'
import ServiceNotificationMailerTracking from './Tracking'

export type ServiceInformationType = {
    notification_name: string
    dns_enable: boolean
    dns_managements: Array<{
        name: string
        type: DNSManagementType
        target: string
    }>
    metadata: Array<{ key: MetadataKeyType; value: string }>
    documentations: Array<{ name: string; link: string }>
}

const ServiceNotifications: React.FC<ServiceNotificationProps> = ({
    serviceID,
}) => {
    const results = useQueries({
        queries: [
            {
                queryKey: [
                    QUERY_KEYS.SERVICES_NOTIFICATIONS_MAILERS,
                    serviceID,
                ],
                queryFn: () => getServiceNotificationMailers({ serviceID }),
            },
            {
                queryKey: [QUERY_KEYS.SERVICES_NOTIFICATIONS, serviceID],
                queryFn: () => getServiceNotifications({ serviceID }),
            },
            {
                queryKey: [QUERY_KEYS.SERVICE, serviceID],
                queryFn: () => getService(Number(serviceID)),
            },
        ],
    })

    const [serviceMailerQuery, serviceNotificationQuery, serviceQuery] = results

    const { t } = useTranslation()
    const [searchParams] = useSearchParams()

    const [actions, setActions] = useState<string[]>([])
    const [editingNotification, setEditingNotification] =
        useState<ServiceNotification | null>(null)

    // Drawer open state
    const isDrawerOpen =
        actions.includes('create-notification-mailer') ||
        editingNotification !== null

    // Track referrer ticket_id from URL
    const [referrerTicketId, setReferrerTicketId] = useState<number | null>(
        null
    )

    // Pre-select notification when clicking from table row
    const [defaultNotificationId, setDefaultNotificationId] = useState<
        number | null
    >(null)

    // Auto-open notification form when action query param is present
    useEffect(() => {
        const action = searchParams.get('action')
        const ticketId = searchParams.get('ticket_id')

        if (action === 'create-notification') {
            // Store ticket_id if present
            if (ticketId) {
                setReferrerTicketId(Number(ticketId))
            }

            // If mailers exist, open delivery form directly; otherwise open create form
            const hasMailers =
                serviceMailerQuery?.data && serviceMailerQuery.data.length > 0
            const hasNotifications =
                serviceNotificationQuery?.data &&
                serviceNotificationQuery.data.length > 0

            if (hasMailers && hasNotifications) {
                setActions((prev) => {
                    if (!prev.includes('delivery-notification-mailer')) {
                        return [...prev, 'delivery-notification-mailer']
                    }
                    return prev
                })
            } else {
                setActions((prev) => {
                    if (!prev.includes('create-notification-mailer')) {
                        return [...prev, 'create-notification-mailer']
                    }
                    return prev
                })
            }
        }
    }, [searchParams, serviceMailerQuery?.data, serviceNotificationQuery?.data])

    const closeDrawer = () => {
        setActions((preActions) =>
            preActions.filter(
                (action) => action !== 'create-notification-mailer'
            )
        )
        setEditingNotification(null)
    }

    if (serviceMailerQuery.isError)
        return <AlertError error={serviceMailerQuery.error} />
    if (serviceNotificationQuery.isError)
        return <AlertError error={serviceNotificationQuery.error} />
    if (
        serviceMailerQuery.isPending ||
        serviceNotificationQuery.isPending ||
        serviceQuery.isPending
    )
        return <LoadingBox />

    return (
        <>
            {actions?.includes('delivery-notification-mailer') && (
                <ServiceNotificationMailer
                    serviceID={serviceID}
                    notifications={serviceNotificationQuery?.data}
                    mailers={serviceMailerQuery?.data}
                    referrerTicketId={referrerTicketId}
                    defaultNotificationId={defaultNotificationId}
                    onClose={() => {
                        setActions((preActions) => [
                            ...(preActions?.filter(
                                (action) =>
                                    action !== 'delivery-notification-mailer'
                            ) || []),
                        ])
                        setReferrerTicketId(null)
                        setDefaultNotificationId(null)
                    }}
                />
            )}

            {/* Right Drawer for Create/Edit Notification */}
            <Sheet
                open={isDrawerOpen}
                onOpenChange={(open) => !open && closeDrawer()}
            >
                <SheetContent
                    side="right"
                    className="sm:max-w-4xl w-full overflow-y-auto p-0"
                    hideCloseButton
                >
                    <SheetHeader className="px-6 pt-6 pb-0">
                        <div className="flex items-center gap-2">
                            <Server className="h-5 w-5 text-primary" />
                            <SheetTitle className="text-base">
                                {editingNotification
                                    ? t('edit-service-notification')
                                    : t('create-service-notifications')}
                            </SheetTitle>
                        </div>
                        <SheetDescription className="text-xs">
                            {editingNotification
                                ? t('edit-service-notification-description')
                                : t('create-service-notifications-description')}
                        </SheetDescription>
                    </SheetHeader>
                    <div className="px-6 pb-6">
                        <Create
                            onClose={closeDrawer}
                            serviceID={serviceID}
                            productGroupName={
                                serviceQuery?.data?.product_group_name
                            }
                            editData={editingNotification}
                            isDrawer
                        />
                    </div>
                </SheetContent>
            </Sheet>

            <List
                serviceID={serviceID}
                notifications={serviceNotificationQuery?.data || []}
                mailers={serviceMailerQuery?.data || []}
                onEdit={(notification) => {
                    setActions((preActions) =>
                        preActions.filter(
                            (action) => action !== 'create-notification-mailer'
                        )
                    )
                    setEditingNotification(notification)
                }}
                onSendEmail={(notificationId) => {
                    setDefaultNotificationId(notificationId)
                    setActions((prev) => {
                        if (!prev.includes('delivery-notification-mailer')) {
                            return [...prev, 'delivery-notification-mailer']
                        }
                        return prev
                    })
                }}
                onCreate={() =>
                    setActions((prev) => [
                        ...prev,
                        'create-notification-mailer',
                    ])
                }
                onDelivery={() =>
                    setActions((prev) => [
                        ...prev,
                        'delivery-notification-mailer',
                    ])
                }
                isDrawerOpen={isDrawerOpen}
                isDeliveryOpen={actions.includes(
                    'delivery-notification-mailer'
                )}
            />
            <ServiceNotificationMailerTracking serviceID={Number(serviceID)} />
        </>
    )
}

export default ServiceNotifications
